import React from 'react';
import { getAppStatusInfo } from './JobApplicationCard';

function formatFileSize(bytes?: number) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getServiceModeLabel(mode?: string) {
  switch (mode) {
    case 'FULL_TIME':
      return '全职';
    case 'PART_TIME':
      return '兼职';
    case 'INTERNSHIP':
      return '实习';
    case 'PROJECT':
      return '外包/项目';
    default:
      return mode || '';
  }
}

export interface ResumeSnapshotData {
  appId?: string;
  appStatus?: string;
  applicantName?: string;
  snapshot: any;
}

export interface ResumeSnapshotModalProps {
  data: ResumeSnapshotData | null;
  reviewingAppId?: string | null;
  onClose: () => void;
  onReview: (appId: string, accepted: boolean) => void;
}

export default function ResumeSnapshotModal({
  data,
  reviewingAppId,
  onClose,
  onReview,
}: ResumeSnapshotModalProps) {
  if (!data || !data.snapshot) return null;

  const { appId, appStatus, applicantName, snapshot } = data;
  const statusInfo = getAppStatusInfo(appStatus);

  const providerProfile = snapshot.provider_profile || {};
  const contact = snapshot.contact || {};
  const expectedSalary = snapshot.expected_monthly_salary;
  const resumeFile = snapshot.resume_file;

  const displayName = providerProfile.display_name || snapshot.name || applicantName || '求职者';
  const phone = contact.phone || snapshot.phone;
  const email = contact.email || snapshot.email;
  const wechatId = contact.wechat_id || snapshot.wechat_id;
  const intro = providerProfile.intro || snapshot.self_introduction;
  const serviceScope = providerProfile.service_scope;
  const serviceMode = getServiceModeLabel(providerProfile.service_mode);
  const skillTags: string[] = providerProfile.skill_tags || snapshot.skill_tags || [];
  const portfolioList: any[] = providerProfile.portfolio || snapshot.portfolio || [];

  // 旧格式兼顾
  const experienceList: any[] = snapshot.experience || [];
  const educationList: any[] = snapshot.education || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <i className="ri-user-3-line text-primary-600"></i>
            {displayName} 的投递简历快照
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-slate-800 text-xs">
          {/* 基本联系信息 */}
          <div className="bg-slate-50 p-4 rounded-xl space-y-2 border border-slate-100">
            <h4 className="font-bold text-slate-900 text-sm mb-2 border-b border-slate-200/60 pb-1">
              基本信息
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600">
              <div>姓名：<span className="font-semibold text-slate-900">{displayName}</span></div>
              <div>期望月薪：<span className="font-semibold text-emerald-600">{expectedSalary ? `¥${expectedSalary}` : '未填写'}</span></div>
              <div>手机号：<span className="font-medium text-slate-800">{phone || '未填写'}</span></div>
              <div>微信号：<span className="font-medium text-slate-800">{wechatId || '未填写'}</span></div>
              <div>电子邮箱：<span className="font-medium text-slate-800">{email || '未填写'}</span></div>
              {serviceMode && <div>服务模式：<span className="font-medium text-slate-800">{serviceMode}</span></div>}
              {serviceScope && <div>服务范围：<span className="font-medium text-slate-800">{serviceScope}</span></div>}
            </div>

            {skillTags.length > 0 && (
              <div className="pt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-slate-500">技能标签：</span>
                {skillTags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-[11px] font-medium border border-primary-100">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 简历文件资源 */}
          {resumeFile && (
            <div className="bg-blue-50/60 p-4 rounded-xl border border-blue-100/80 space-y-2">
              <h4 className="font-bold text-blue-950 text-sm flex items-center gap-1.5">
                <i className="ri-file-download-line text-blue-600"></i>
                简历文件附件
              </h4>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white p-3 rounded-lg border border-blue-100">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">
                    {resumeFile.display_name || resumeFile.file_name || '简历文件'}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    文件格式: {resumeFile.file_type || '未定义'} {resumeFile.file_size ? `· ${formatFileSize(resumeFile.file_size)}` : ''}
                  </p>
                </div>
                {resumeFile.access_url && (
                  <a
                    href={resumeFile.access_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap shadow-xs"
                  >
                    <i className="ri-external-link-line"></i>
                    在线查看 / 下载简历
                  </a>
                )}
              </div>
            </div>
          )}

          {/* 个人简介 / 自我介绍 */}
          {intro && (
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-1.5 flex items-center gap-1">
                <i className="ri-user-smile-line text-primary-600"></i> 个人简介
              </h4>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed bg-white p-3 rounded-xl border border-slate-100">
                {intro}
              </p>
            </div>
          )}

          {/* 作品集 */}
          {portfolioList.length > 0 && (
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2 flex items-center gap-1">
                <i className="ri-folder-open-line text-primary-600"></i> 作品集
              </h4>
              <div className="space-y-2.5">
                {portfolioList.map((pf, idx) => {
                  const attachments: any[] = pf.attachments || (pf.attachments ? [pf.attachments] : []);
                  return (
                    <div key={pf.id || idx} className="p-3.5 rounded-xl border border-slate-100 bg-white space-y-2">
                      <div className="font-bold text-slate-900 text-xs">{pf.title || pf.name || '作品展示'}</div>
                      {pf.intro && <p className="text-slate-600 text-[11px] leading-relaxed">{pf.intro}</p>}

                      {attachments.length > 0 && (
                        <div className="space-y-1 pt-1 border-t border-slate-50">
                          {attachments.map((att, aidx) => (
                            <div key={att.upload_asset_id || aidx} className="flex items-center justify-between text-[11px] bg-slate-50 p-2 rounded-lg">
                              <span className="text-slate-700 font-medium truncate max-w-[280px]">
                                <i className="ri-attachment-line text-slate-400 mr-1"></i>
                                {att.display_name || att.file_name || '作品附件'}
                              </span>
                              {att.access_url && (
                                <a
                                  href={att.access_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:underline font-medium flex items-center gap-0.5 ml-2"
                                >
                                  查看作品 <i className="ri-external-link-line"></i>
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 工作/项目经验（兼容旧结构） */}
          {experienceList.length > 0 && (
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2 flex items-center gap-1">
                <i className="ri-briefcase-line text-primary-600"></i> 工作 / 项目经验
              </h4>
              <div className="space-y-3">
                {experienceList.map((exp, idx) => (
                  <div key={idx} className="p-3.5 rounded-xl border border-slate-100 bg-white space-y-1">
                    <div className="flex items-center justify-between font-semibold text-slate-900">
                      <span>{exp.organization} · {exp.position}</span>
                      <span className="text-[11px] text-slate-400 font-normal">
                        {exp.start_date || ''} ~ {exp.end_date || '至今'}
                      </span>
                    </div>
                    {exp.description && (
                      <p className="text-slate-600 whitespace-pre-wrap leading-relaxed text-[11px]">
                        {exp.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 教育经历（兼容旧结构） */}
          {educationList.length > 0 && (
            <div>
              <h4 className="font-bold text-slate-900 text-sm mb-2 flex items-center gap-1">
                <i className="ri-graduation-cap-line text-primary-600"></i> 教育经历
              </h4>
              <div className="space-y-2">
                {educationList.map((edu, idx) => (
                  <div key={idx} className="p-3 rounded-xl border border-slate-100 bg-white flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-slate-900">{edu.school}</span>
                      <span className="text-slate-500 ml-2">{edu.major} ({edu.degree || '学历'})</span>
                    </div>
                    <span className="text-[11px] text-slate-400">
                      {edu.start_date || ''} ~ {edu.end_date || ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {appId && statusInfo.isPending ? (
              <>
                <button
                  type="button"
                  disabled={reviewingAppId === appId}
                  onClick={() => onReview(appId, true)}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-all cursor-pointer shadow-xs disabled:opacity-50 flex items-center gap-1"
                >
                  {reviewingAppId === appId ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-check-line"></i>
                  )}
                  通过
                </button>
                <button
                  type="button"
                  disabled={reviewingAppId === appId}
                  onClick={() => onReview(appId, false)}
                  className="px-4 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1"
                >
                  <i className="ri-close-line"></i>
                  拒绝
                </button>
              </>
            ) : (
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${statusInfo.color}`}>
                状态：{statusInfo.label}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-semibold transition-all cursor-pointer"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
